import { TabloProjectStructure } from '@ui/tablo-generator/types';

type JsonRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is JsonRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const getElementIndex = (element: unknown, originalIndex: number): number => {
  if (!isRecord(element) || !isRecord(element.config)) {
    return originalIndex;
  }

  const index = element.config.index;
  return typeof index === 'number' && Number.isFinite(index)
    ? index
    : originalIndex;
};

const normalizeValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(normalizeValue);
  }

  if (!isRecord(value)) {
    return value;
  }

  return Object.keys(value)
    .sort()
    .reduce<JsonRecord>((normalized, key) => {
      const property = value[key];
      if (typeof property !== 'undefined') {
        normalized[key] = normalizeValue(property);
      }

      return normalized;
    }, {});
};

const normalizeStructureElement = (
  element: unknown,
  index: number,
): unknown => {
  const elementRecord = isRecord(element) ? element : {};
  const config = isRecord(elementRecord.config) ? elementRecord.config : {};

  return normalizeValue({
    ...elementRecord,
    config: {
      ...config,
      index,
      rotation: config.rotation ?? 0,
    },
  });
};

export const normalizeTabloStructureForSignature = (
  structure: TabloProjectStructure,
): unknown[] =>
  structure
    .map((element, originalIndex) => ({
      element,
      originalIndex,
      index: getElementIndex(element, originalIndex),
    }))
    .sort(
      (left, right) =>
        left.index - right.index || left.originalIndex - right.originalIndex,
    )
    .map(({ element }, index) => normalizeStructureElement(element, index));

export const createTabloStructureSignature = (
  structure: TabloProjectStructure,
): string => JSON.stringify(normalizeTabloStructureForSignature(structure));
